import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { Box, Button, Icon, Input, Label, Text } from '@zillow/constellation-10';
import { IconErrorFilled } from '@zillow/constellation-icons';

import { logError } from '@zg-rentals/log-error';
import { stringHelper } from '@zg-rentals/rental-manager-env';
import { DatarouterError } from '@zg-rentals/rental-manager-env';
import { useRentalManagerUserEmailVerificationWebApiHandler_sendVerificationCode } from '@zg-rentals/ts-rental-manager-api-client-react-query';

import numberUtils from '../../../../utils/numberUtils';

import StringConstants from '../../../../constants/StringConstants';
import { MODES, VERIFICATION_STEPS } from '../constants';

import MaxAttemptsError from './MaxAttemptsError';
import VerificationBodyTemplate from './VerificationBodyTemplate';

import type { ChangeEvent, KeyboardEvent } from 'react';
import type { GetUserPropertyDetailsResponseWebDto } from '@zg-rentals/ts-rental-manager-api-client';
import type { MODES_VALUES } from '../constants';

const CONFIG = {
  phone: {
    heading: 'Add your phone number',
    subheading:
      'To help protect your identity, Zillow will send a verification code to this number. You can choose whether to show the number on your listing.',
    ctaLabel: 'Your phone number:',
    type: 'tel',
    value: (val: string) => numberUtils.formatPhoneNumberInput(val),
    ariaText: 'example-telephone',
    submitButtonLabel: 'Next',
  },
  email: {
    heading: 'Add your email address and get verification code',
    subheading: 'To help protect your identity, Zillow will send a verification code to this email',
    ctaLabel: 'Your email address:',
    type: 'email',
    value: (val: string) => val,
    ariaText: 'example-email',
    submitButtonLabel: 'Send verification code',
  },
};

type Props = {
  currentStepIndex: number;
  incrementStep: (step?: string) => void;
  onContentValueAdded?: (inputValue: string) => Promise<void>;
  saveContentToVerify: (inputValue: string) => void;
  verificationMode?: MODES_VALUES;
  listingDetails?: GetUserPropertyDetailsResponseWebDto;
};

const AddPhoneNumberOrEmail = ({
  currentStepIndex,
  incrementStep,
  onContentValueAdded,
  saveContentToVerify,
  verificationMode = MODES.PHONE,
  listingDetails,
}: Props) => {
  const contactEmail = listingDetails?.email;
  const contactPhone = listingDetails?.phone;
  const listingAlias = listingDetails?.listingAlias;
  const preferredContactEmail = listingDetails?.preferredContactEmail;

  const initialInputValue = useMemo(() => {
    switch (verificationMode) {
      case MODES.PHONE:
        return contactPhone;
      case MODES.EMAIL:
        return contactEmail || preferredContactEmail;
      default:
        return '';
    }
  }, [verificationMode, contactPhone, contactEmail, preferredContactEmail]);

  const [inputValue, setInputValue] = useState(initialInputValue || '');
  const [error, setError] = useState<Partial<DatarouterError>>({});
  const [isLoading, setIsLoading] = useState(false);

  const inputRef = useRef<HTMLInputElement>(null);

  const { mutate: sendVerificationCode } = useRentalManagerUserEmailVerificationWebApiHandler_sendVerificationCode();

  const isButtonDisabled = error?.code === StringConstants.SS_ERRORS.attempt_limit_reached;

  useEffect(() => {
    inputRef?.current?.select();
  }, []);

  const handlePhoneNextClick = useCallback(
    (phone: string) => {
      const cleanPhoneNumber = numberUtils.cleanPhoneNumber(phone);

      if (cleanPhoneNumber.length < 10) {
        setError({ message: StringConstants.SS_ERRORS.invalid_phone_number });
        inputRef?.current?.focus();

        return;
      }

      if (cleanPhoneNumber === contactPhone) {
        incrementStep(VERIFICATION_STEPS.VERIFICATION_SUCCESS);

        return;
      }

      if (onContentValueAdded) {
        setIsLoading(true);

        onContentValueAdded(cleanPhoneNumber)
          .then(() => incrementStep(VERIFICATION_STEPS.VERIFICATION_SUCCESS))
          .catch(({ code, message }: DatarouterError) => {
            if (code === StringConstants.SS_ERRORS.confirm_phone) {
              saveContentToVerify(cleanPhoneNumber);
              incrementStep();

              return;
            }

            setError({ code, message });
            inputRef?.current?.focus();
          })
          .finally(() => setIsLoading(false));
      }
    },
    [contactPhone, incrementStep, onContentValueAdded, saveContentToVerify],
  );

  const handleEmailNextClick = useCallback(
    (email: string) => {
      if (!stringHelper.validateEmail(email)) {
        setError({ message: StringConstants.SS_ERRORS.invalid_email_address });
        inputRef?.current?.focus();

        return;
      }

      if (email === contactEmail) {
        incrementStep(VERIFICATION_STEPS.VERIFICATION_SUCCESS);

        return;
      }

      if (onContentValueAdded) {
        setIsLoading(true);

        onContentValueAdded(email)
          .then(() => incrementStep(VERIFICATION_STEPS.VERIFICATION_SUCCESS))
          .catch((err: DatarouterError) => {
            if (
              listingAlias !== undefined &&
              (err?.code === StringConstants.SS_ERRORS.email_verification_pending ||
                err?.code === StringConstants.SS_ERRORS.email_verification_required)
            ) {
              saveContentToVerify(email);
              sendVerificationCode(
                { getParams: { listingAlias, email: email } },
                {
                  onSuccess: () => incrementStep(),
                  onError: (err) => {
                    const errorCode = DatarouterError.getDisplayCode(err);
                    const errorMessage = DatarouterError.getDisplayMessage(err);

                    if (errorMessage) {
                      setError({
                        code: errorCode,
                        message: errorMessage,
                      });
                      logError({
                        error: err,
                        context: 'requestVerificationCode',
                      });

                      inputRef?.current?.focus();
                      setIsLoading(false);
                    }
                  },
                },
              );

              return;
            }

            setError({ code: err?.code, message: err?.message || '' });
            inputRef?.current?.focus();
            setIsLoading(false);
          });
      }
    },
    [contactEmail, incrementStep, listingAlias, onContentValueAdded, saveContentToVerify, sendVerificationCode],
  );

  const handleNextClick = () => {
    switch (verificationMode) {
      case MODES.PHONE:
        handlePhoneNextClick(inputValue);

        break;
      case MODES.EMAIL:
        handleEmailNextClick(inputValue);

        break;
      default:
        return;
    }
  };

  const onChangeHandler = (e: ChangeEvent<HTMLInputElement>) => {
    setError({});
    setInputValue(e.target.value);
  };

  const onKeyDownHandler = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleNextClick();
    }
  };

  const renderErrorMessage = () => {
    if (isButtonDisabled) {
      return <MaxAttemptsError verificationMode={verificationMode} />;
    }

    return (
      <Box css={{ marginTop: '400' }}>
        <Box css={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Icon
            size="md"
            css={{ color: 'icon.feedback.critical.hero', marginRight: '200' }}
            render={<IconErrorFilled />}
            title={error?.message}
          />
          <Text
            id={`${verificationMode}-error`}
            css={{ color: 'text.feedback.critical.strong', lineHeight: '18px', textAlign: 'center' }}
            asChild
          >
            <p>{`${error?.message}`}</p>
          </Text>
        </Box>
      </Box>
    );
  };

  return (
    <VerificationBodyTemplate
      heading={`Step ${currentStepIndex + 1} - ${CONFIG[verificationMode].heading}`}
      subheading={CONFIG[verificationMode].subheading}
    >
      <Box
        css={{
          marginBlock: '400',
          marginInline: 'auto',
          width: verificationMode === MODES.EMAIL ? '250px' : '200px',
          lg: {
            marginBlock: '800',
          },
        }}
      >
        <Label
          size="lg"
          htmlFor={`${verificationMode}Input`}
          css={{ display: 'block', marginBottom: { base: '200', lg: '400' } }}
        >
          {CONFIG[verificationMode].ctaLabel}
        </Label>
        <Box css={{ marginBottom: '200' }}>
          <Input
            id={`${verificationMode}Input`}
            data-testid={`${verificationMode}Input`}
            className="fs-mask"
            error={!!error?.message}
            onChange={onChangeHandler}
            onKeyDown={onKeyDownHandler}
            ref={inputRef}
            type={CONFIG[verificationMode].type}
            value={CONFIG[verificationMode].value(inputValue)}
            aria-describedby={error?.message ? `${verificationMode}-error` : CONFIG[verificationMode].ariaText}
          />
        </Box>
      </Box>

      <Button
        data-testid={`${verificationMode}NextButton`}
        tone="brand"
        emphasis="filled"
        disabled={isButtonDisabled}
        loading={isLoading}
        onClick={handleNextClick}
        size="md"
        css={{
          minWidth: '200px',
          display: 'block',
          margin: '0 auto',
        }}
      >
        {CONFIG[verificationMode].submitButtonLabel}
      </Button>

      {Boolean(error?.message) && (
        <Box css={{ marginBlock: '200' }} aria-live="assertive">
          {renderErrorMessage()}
        </Box>
      )}
    </VerificationBodyTemplate>
  );
};

export default AddPhoneNumberOrEmail;
